// 発表会（RECITAL）管理API クライアント ── S2 設定＋演目構成（recital_spec_draft.md §7.2）
// 既存の lessonV2Api と同じ規約：REACT_APP_API_URL ベース・credentials include・error は data.error を投げる。
const API = process.env.REACT_APP_API_URL;
const YEAR = 2026;

const handle = async (res) => {
  let data = null;
  try {
    data = await res.json();
  } catch (_) {
    // ignore
  }
  if (!res.ok) {
    const err = new Error((data && data.error) || `HTTP ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return data;
};

const qs = `?year=${YEAR}`;

// --- 発表会設定 ---
export const getRecitalEvent = () =>
  fetch(`${API}/api/admin/recital/event/${qs}`, { credentials: "include" }).then(handle);

export const updateRecitalEvent = (payload) =>
  fetch(`${API}/api/admin/recital/event/${qs}`, {
    method: "PUT",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }).then(handle);

// メインビジュアルは既存の汎用アップロード（S3・WebP・サムネ）を流用し id を得る（§9.2）
export const uploadImage = (file) => {
  const fd = new FormData();
  fd.append("images", file);
  return fetch(`${API}/api/image_upload/`, {
    method: "POST",
    credentials: "include",
    body: fd,
  }).then(handle);
};

// --- 演目構成 ---
export const getRecitalPrograms = () =>
  fetch(`${API}/api/admin/recital/programs/${qs}`, { credentials: "include" }).then(handle);

export const toggleRecitalClass = (placementId, showOnEntry) =>
  fetch(`${API}/api/admin/recital/programs/class-toggle/${qs}`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ placement_id: placementId, show_on_entry: showOnEntry }),
  }).then(handle);

// 特別演目の追加。extra で行種別/部/所要（MC・休憩・進行行）も指定可（§⑨ 点呼表データ源）
export const addRecitalSpecial = (title, instructorId, extra = {}) =>
  fetch(`${API}/api/admin/recital/programs/${qs}`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ title, instructor_id: instructorId || null, ...extra }),
  }).then(handle);

export const patchRecitalProgram = (programId, patch) =>
  fetch(`${API}/api/admin/recital/programs/${programId}/${qs}`, {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  }).then(handle);

export const deleteRecitalProgram = (programId) =>
  fetch(`${API}/api/admin/recital/programs/${programId}/${qs}`, {
    method: "DELETE",
    credentials: "include",
  }).then(handle);

// --- 参加者リスト（S3・§7.2）---
// 全件取得（tab/q はクライアント側でフィルタ＝集計カウントに全件必要）
export const getRecitalEntries = () =>
  fetch(`${API}/api/admin/recital/entries/${qs}`, { credentials: "include" }).then(handle);

// 個別編集（participation/payment_status/program_ids/absent_reason）。対象は customer_id
export const patchRecitalEntry = (customerId, patch) =>
  fetch(`${API}/api/admin/recital/entries/${customerId}/${qs}`, {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  }).then(handle);

// 参加申込の取り消し（Entry 論理削除＝未回答へ。出演クラス削除・写真論理削除・支払い状況は保持）
export const cancelRecitalEntry = (customerId) =>
  fetch(`${API}/api/admin/recital/entries/${customerId}/${qs}`, {
    method: "DELETE",
    credentials: "include",
  }).then(handle);

// 支払い一括更新（チェック行→ paid/unpaid/refunded）
export const bulkRecitalPayments = (customerIds, payment) =>
  fetch(`${API}/api/admin/recital/payments/${qs}`, {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ customer_ids: customerIds, payment }),
  }).then(handle);

// --- 演目順（S4・§7.2）---
// 演目順データ（display_order順・出演者付き）＋特別演目ピッカー用の参加者一覧
export const getRecitalOrder = () =>
  fetch(`${API}/api/admin/recital/order/${qs}`, { credentials: "include" }).then(handle);

// 並び順＋部の一括更新（programs＝表示順の[{id, part}]配列・partは1始まり）
export const updateRecitalOrder = (programs) =>
  fetch(`${API}/api/admin/recital/order/${qs}`, {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ programs }),
  }).then(handle);

// --- 点呼表（S5・§⑨）---
// 点呼表①データ（演目順・開始時刻積み上げ・上手下手集計・掛け持ち）。②③④CSVはfront生成。
export const getRecitalRollcall = () =>
  fetch(`${API}/api/admin/recital/rollcall/${qs}`, { credentials: "include" }).then(handle);

// --- 講師ログインURL（S6・§10.5🟦12）---
// インストラクター毎のトークン取得（再発行なし）。out: {instructors:[{instructor_id,name,token}]}
export const getInstructorTokens = () =>
  fetch(`${API}/api/admin/recital/instructor-tokens/${qs}`, { credentials: "include" }).then(handle);

// 演目の会員出演者を差し替え（特別演目への手動追加。out: {program}）
export const updateRecitalCast = (programId, customerIds) =>
  fetch(`${API}/api/admin/recital/programs/${programId}/cast/${qs}`, {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ customer_ids: customerIds }),
  }).then(handle);

// --- パンフレット写真／パンフレット（S7・§⑪・🟦11）---
// パンフレット写真一覧（参加者・フリガナ五十音順・未提出は空）。out: {rows[], summary}
export const getRecitalPhotos = () =>
  fetch(`${API}/api/admin/recital/photos/${qs}`, { credentials: "include" }).then(handle);

// 代理アップロード（4:3 クロップ済み Blob）。out: {row}
export const uploadRecitalPhoto = (customerId, blob) => {
  const fd = new FormData();
  fd.append("file", blob, "photo.jpg");
  return fetch(`${API}/api/admin/recital/photos/${customerId}/${qs}`, {
    method: "POST",
    credentials: "include",
    body: fd,
  }).then(handle);
};

// パンフレット出力（CSV＋画像ZIP）。Blob を返しクライアントでDL（handle は使わない＝バイナリ）
export const exportRecitalPhotos = async () => {
  const res = await fetch(`${API}/api/admin/recital/photos/export/${qs}`, {
    credentials: "include",
  });
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      msg = (await res.json()).error || msg;
    } catch (_) {
      // ignore
    }
    throw new Error(msg);
  }
  return res.blob();
};
